import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridRowSelectedEvent } from '@inavista/components/grid';
import { environment } from 'projects/service/src/environments/environment';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { UserRoles } from 'projects/service/src/app/shared/models/app.model';
import {
  RemoteSearchGridApi, RemoteSearchParam, provideRemoteSearchGrid, remoteSearchRow,
} from 'projects/service/src/app/shared/utils/remote-search-grid-api';
import {
  CUSTOMER_ADVANCED_SEARCH_URL, CUSTOMER_COLUMNS, PROCEED_ACTION, PROCEED_ACTION_ICONS, SOLD_TO_BP_COLUMNS,
  buildCustomerAdvancedSearchGridConfig,
} from './config-advanced-search.grid.config';

const BASE_URL = environment.apiUrl;

// The old grid's content was window.innerHeight - 350 tall, with its toolbar and header above it and the
// "Total Records" bar below. The inavista grid draws all of that inside its own box.
const GRID_CHROME_PX = 124;

@Component({
    selector: 'app-config-advanced-search',
    templateUrl: './config-advanced-search.component.html',
    styleUrls: ['./config-advanced-search.component.scss'],
    // The grid reads the old endpoints through RemoteSearchGridApi (same request, same response).
    providers: [...provideRemoteSearchGrid()],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ConfigAdvancedSearchComponent implements OnInit {
  isExpanded: boolean = true;
  validationApplied: boolean = false;
  gridConfig: GridConfig = null;
  readonly actionIcons = PROCEED_ACTION_ICONS;
  selectedRowData: any;
  gridHeight: number = window.innerHeight - 350 + GRID_CHROME_PX;
  @Input() gridType: any = 'config';

  USER;

  @Input() serviceDepartment;
  @Input() company;

  @Output() onClose = new EventEmitter();
  @ViewChild('serviceOrderGrid') serviceOrderGrid: InavistaGrid;

  userRoles: any;

  city = ''; state = ''; zipCode = ''; customerName = ''; address = '';
  private readonly gridApi = inject(RemoteSearchGridApi);

  constructor(private commonService: CommonService) {
    this.USER = commonService.USER;
  }

  ngOnInit(): void {
    this.USER = this.commonService.USER || this.commonService.getUSER();
    this.company = this.company || this.USER?.company || localStorage.getItem('company');
    this.serviceDepartment = this.serviceDepartment || this.USER?.serviceDepartment;

    this.userRoles = UserRoles;
    const isConfig = this.gridType === 'config';
    const columns = isConfig ? CUSTOMER_COLUMNS : SOLD_TO_BP_COLUMNS;
    // Until a search, no endpoint and no rows (ISC: the old grid started with data = []).
    this.gridApi.set(CUSTOMER_ADVANCED_SEARCH_URL, {
      endpoint: '', columns, searchFields: columns.map(([c]) => c), parameters: null,
    });
    const isIsc = this.isIsc();
    this.gridConfig = buildCustomerAdvancedSearchGridConfig(isConfig,
      isIsc ? 'No customers to show. Fill in Customer Name, City and State, then click Search.' : 'No records to display');
    if (!isIsc) {
      this.getCustomers(null, true);
    }
  }

  private isIsc() {
    return this.USER?.role === UserRoles.iscManager || this.USER?.role === UserRoles.IscSupervisor || this.USER?.role === UserRoles.isc;
  }

  validate() {
    this.validationApplied = true;
    return this.customerName && this.city && this.state;
  }

  // The Query parameters of the old grid, as the CustomAdaptor sent them: isIsc is removed and turns the search
  // fields into 'contains'.
  setQuery(isIsc): RemoteSearchParam[] {
    const company = this.company || this.commonService.USER?.company || this.USER?.company || localStorage.getItem('company') || '';
    const serviceDept = this.serviceDepartment || this.commonService.USER?.serviceDepartment || this.USER?.serviceDepartment || '';

    const params: RemoteSearchParam[] = [{ key: 'company', value: company }];
    if (this.gridType !== 'config') {
      params.push({ key: '$orderBy', value: 'tax' });
    }
    if (isIsc) {
      params.push({ key: 'serviceDepartment', value: serviceDept });
      this.city && params.push({ key: 'city', value: this.city, oprtr: 'contains' });
      this.state && params.push({ key: 'stateProvince', value: this.state, oprtr: 'contains' });
      this.zipCode && params.push({ key: 'zipCode', value: this.zipCode, oprtr: 'contains' });
      this.customerName && params.push({ key: 'bpDescription', value: this.customerName, oprtr: 'contains' });
      this.address && params.push({ key: 'address', value: this.address, oprtr: 'contains' });
    }
    return params;
  }

  getCustomers(event, onInit?: boolean) {
    if (this.validate() || onInit) {
      const isIsc = this.isIsc();
      const getCustomerApi = isIsc ? 'SoldToBP/GetISCCustomerSearchView' : 'SoldToBP/GetCustomerSearchView'
      const api = this.gridType === 'config' ? getCustomerApi : 'SoldToBP/GetSoldToBP';
      const columns = this.gridType === 'config' ? CUSTOMER_COLUMNS : SOLD_TO_BP_COLUMNS;

      this.gridApi.set(CUSTOMER_ADVANCED_SEARCH_URL, {
        endpoint: `${BASE_URL}${api}`, columns, searchFields: columns.map(([c]) => c), parameters: this.setQuery(isIsc),
      });
      // On open the grid loads by itself; a Search reloads it.
      if (!onInit) this.serviceOrderGrid?.refresh();
    }
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === PROCEED_ACTION) {
      this.onClose.emit(remoteSearchRow(event.row));
    }
  }

  close() {
    this.validationApplied = false;
    this.onClose.emit(null);
  }

  proceed() {
    this.onClose.emit(this.selectedRowData);
  }

  rowSelected(event: GridRowSelectedEvent) {
    this.selectedRowData = remoteSearchRow(event.row);
  }

}
